//path: #hooks/useSession.js

// --- DÉBUT DU CODE ANCIEN (COMMENTÉ) ---
/*
import { useState, useEffect } from "react";
import Config from "@config";
import WalletController from "@controllers/wallet.controller.js";

const SESSION_KEY = Config.LOCAL_STORAGE_KEY;
let sessionCache = null;
let setSessionState = null;

// Hook React pour gérer l'état de la session (ancienne implémentation)
// Ce hook initialise l'état de la session et fournit des fonctions pour la manipuler.
// Il utilise un cache global (sessionCache) et une fonction de mise à jour d'état globale (setSessionState)
// pour tenter de synchroniser l'état de la session à travers l'application.
export const useSession = () => {
  // Initialise l'état local du hook avec la session actuelle.
  const [session, setSession] = useState(getSession());

  // Effet de bord pour initialiser la session au montage du composant.
  useEffect(() => {
    initSession();
  }, []); // S'exécute une seule fois au montage.

  // Effet de bord pour logger l'état de la session à chaque mise à jour.
  // Ce bloc était utilisé pour le débogage intensif.
  useEffect(() => {
    // ------------------ cmt 832886 ------------------
    console.log('=== session === useSession.js === key: 662993 ===');
    console.dir(session, { depth: null, colors: true });
    console.log('=================================');
    // -------------------------------------------------
  }, [session]); // S'exécute à chaque changement de l'objet 'session'.

  // Stocke la fonction de mise à jour de l'état local dans une variable globale.
  // Cela permet aux fonctions externes (comme setSession, setInSession, clearSession)
  // de déclencher des re-rendus des composants utilisant useSession.
  setSessionState = setSession;

  // Retourne l'état de la session et les fonctions de manipulation.
  return {
    session,
    clearSession,
    setInSession,
    user: getWrapper.user, // Accesseur pour les données utilisateur.
    getSession, // Fonction pour obtenir la session.
  };
};

// Objet wrapper pour accéder aux données utilisateur de manière contrôlée.
const getWrapper = {
  get user() {
    return getUser();
  },
};

// Fonction interne pour récupérer les données utilisateur depuis le cache de session.
const getUser = () => {
  if (!sessionCache?.user) {
    return null;
  }
  return sessionCache.user;
};

// Fonction pour récupérer la session stockée dans le localStorage (ancienne version).
// Elle utilise un cache pour éviter des lectures répétées du localStorage.
// export const getSession = () => { // Cette ligne était commentée dans le code original
//   if (sessionCache) {
//     return sessionCache;
//   }
//   const sessionStr = localStorage.getItem(SESSION_KEY);
//   sessionCache = sessionStr ? JSON.parse(sessionStr) : null;
//   return sessionCache;
// };

// Fonction pour initialiser la session.
// Elle tente de récupérer la session et de la définir dans l'état du hook.
const initSession = () => {
  if (sessionCache) {
    return sessionCache;
  }
  const initialSession = getSession();
  try {
    setSessionState(initialSession);
  } catch (error) {
    console.log("=== error === useSession.js === key: 587484 ===");
    console.dir(error, { depth: null, colors: true });
    console.log("=================================");
  }
};

// Fonction pour récupérer la session stockée dans le localStorage (version avec forceRefresh).
// Permet de forcer une relecture depuis localStorage, ignorant le cache.
export const getSession = (forceRefresh = false) => {
  if (sessionCache && !forceRefresh) {
    return sessionCache;
  }
  // Sinon, charger depuis le localStorage
  const sessionStr = localStorage.getItem(SESSION_KEY);
  sessionCache = sessionStr ? JSON.parse(sessionStr) : null;
  return sessionCache;
};
*/
// --- FIN DU CODE ANCIEN (COMMENTÉ) ---




import { useState, useEffect, useCallback } from "react"
import Config from "../config/config"

const SESSION_KEY = Config.LOCAL_STORAGE_KEY
const SESSION_UPDATE_EVENT = "sessionUpdateEvent" // Événement personnalisé pour les mises à jour dans l'onglet

// Fonction interne pour récupérer la session depuis localStorage
const getSessionFromLocalStorage = () => {
  const sessionStr = localStorage.getItem(SESSION_KEY)
  return sessionStr ? JSON.parse(sessionStr) : null
}

// Fonction interne pour enregistrer la session dans localStorage
const setSessionInLocalStorage = (session) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

// Fonction interne pour supprimer la session de localStorage
const clearSessionFromLocalStorage = () => {
  localStorage.removeItem(SESSION_KEY)
}

/**
 * Fonction publique pour récupérer la session. Lit directement depuis localStorage.
 * @returns {object|null} La session actuelle.
 */
export const getSession = () => {
  return getSessionFromLocalStorage()
}

/**
 * Fonction publique pour définir la session. Met à jour localStorage et notifie les écouteurs.
 * Cette fonction NE DOIT PAS appeler directement setSessionState.
 * @param {object} session - L'objet session à sauvegarder.
 * @returns {object|null} La session mise à jour.
 */
export const setSession = (session) => {
  setSessionInLocalStorage(session)
  // Déclenche un événement personnalisé pour notifier les composants React dans le même onglet
  window.dispatchEvent(new CustomEvent(SESSION_UPDATE_EVENT, { detail: session }))
  return getSessionFromLocalStorage()
}

/**
 * Fonction publique pour définir une clé spécifique dans la session utilisateur.
 * @param {string} key - La clé à mettre à jour (ex. 'firstname', 'lastname', 'email').
 * @param {*} value - La nouvelle valeur.
 * @returns {object|null} La session mise à jour.
 */
export const setInSession = (key, value) => {
  const currentSession = getSessionFromLocalStorage()
  if (!currentSession?.user) {
    return
  }
  // Ajout d’un contrôle pour les clés autorisées
  if (!["firstname", "lastname", "email", "platformToken", "participant"].includes(key)) {
    console.warn(`V0_DEBUG: Tentative de définir une clé de session non supportée : ${key}`)
    return
  }
  if (currentSession.user[key] === value) {
    return
  }
  currentSession.user[key] = value
  setSessionInLocalStorage(currentSession)
  window.dispatchEvent(new CustomEvent(SESSION_UPDATE_EVENT, { detail: currentSession }))
  return currentSession
}

/**
 * Fonction publique pour supprimer la session.
 * @returns {null}
 */
export const clearSession = () => {
  clearSessionFromLocalStorage()
  window.dispatchEvent(new CustomEvent(SESSION_UPDATE_EVENT, { detail: null }))
  return null
}

/**
 * Hook React pour gérer l'état de la session dans les composants.
 * Synchronise l'état React avec localStorage et réagit aux mises à jour.
 * @returns {object}
 * {
 * session: object|null,
 * clearSession: function,
 * setInSession: function,
 * user: object|null,
 * getSession: function, // Getter interne de l’état du hook
 * }
 */
export const useSession = () => {
  // Initialise l’état React à partir de localStorage
  const [session, setSessionState] = useState(() => getSessionFromLocalStorage())

  useEffect(() => {
    // Écouteur pour les événements personnalisés (mises à jour dans le même onglet/fenêtre)
    const handleSessionUpdate = (event) => {
      console.log("V0_DEBUG: === événement de mise à jour de session reçu === useSession.js ===")
      setSessionState(event.detail)
    }

    // Écouteur pour les événements natifs 'storage' (mises à jour depuis d'autres onglets/fenêtres)
    const handleStorageChange = (event) => {
      if (event.key === SESSION_KEY) {
        console.log("V0_DEBUG: === événement de stockage reçu === useSession.js ===")
        setSessionState(getSessionFromLocalStorage())
      }
    }

    window.addEventListener(SESSION_UPDATE_EVENT, handleSessionUpdate)
    window.addEventListener("storage", handleStorageChange)

    // Nettoyage des écouteurs
    return () => {
      window.removeEventListener(SESSION_UPDATE_EVENT, handleSessionUpdate)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, []) // Dépendances vides pour n'exécuter qu'au montage

  // Log de l'état de session du hook
  useEffect(() => {
    console.log("V0_DEBUG: === session === useSession.js === key: 662993 ===")
    console.dir(session, { depth: null, colors: true })
    console.log("V0_DEBUG: =================================")
  }, [session])

  // Getter interne de l’état du hook
  const getSessionHookState = useCallback(() => session, [session])

  return {
    session,
    clearSession,
    setInSession,
    user: session?.user || null,
    getSession: getSessionHookState,
  }
}
